import axios from 'axios';

// API Configuration - Unified Gateway Access
// All requests go through API Gateway for centralized routing and security
const API_GATEWAY_URL = process.env.REACT_APP_API_GATEWAY_URL || 'http://localhost:8080';
const AUTH_SERVICE_URL = process.env.REACT_APP_AUTH_SERVICE_URL || 'http://localhost:8001';
const API_PUBLIC_URL = process.env.REACT_APP_API_PUBLIC_URL || 'http://localhost:8010';

// Token storage key
const TOKEN_STORAGE_KEY = 'sunat_platform_token';

// Auth utilities
export const auth = {
  getToken: (): string | null => {
    return localStorage.getItem(TOKEN_STORAGE_KEY);
  },

  setToken: (token: string): void => {
    localStorage.setItem(TOKEN_STORAGE_KEY, token);
  },

  removeToken: (): void => {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
  },

  isAuthenticated: (): boolean => {
    const token = auth.getToken();
    if (!token) return false;

    try {
      // Simple JWT expiration check (decode payload)
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Math.floor(Date.now() / 1000);
      return payload.exp > currentTime;
    } catch {
      return false;
    }
  }
};

// Create secure axios instance with authentication
const createSecureClient = (baseURL: string) => {
  const client = axios.create({
    baseURL,
    timeout: 10000,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Request interceptor to add auth token
  client.interceptors.request.use(
    (config) => {
      const token = auth.getToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  // Response interceptor to handle auth errors
  client.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        auth.removeToken();
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }
  );

  return client;
};

// Create auth client (no auth required)
const authClient = axios.create({
  baseURL: AUTH_SERVICE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Create API Public client (uses API Key authentication)
const createApiPublicClient = (apiKey: string) => {
  return axios.create({
    baseURL: API_PUBLIC_URL,
    timeout: 30000,
    headers: {
      'Content-Type': 'application/json',
      'X-API-Key': apiKey,
    },
  });
};

// Create unified client that uses API Gateway
export const secureClient = createSecureClient(API_GATEWAY_URL);

// All service-specific clients use the same Gateway endpoint
export const tenantsClient = secureClient;
export const certificatesClient = secureClient;
export const documentsClient = secureClient;
export const customersClient = secureClient;
export const analyticsClient = secureClient;

// API Service interfaces - Sprint 6 Enhanced
export interface Tenant {
  id: string;
  company_name: string;
  ruc: string;
  email: string;
  environment: 'beta' | 'production';
  status: 'active' | 'inactive' | 'suspended';
  created_at: string;
  updated_at?: string;

  // SUNAT specific fields - Sprint 6 (Optional for backwards compatibility)
  razon_social_completa?: string;
  direccion_fiscal?: string;
  ubigeo?: string;
  codigo_local_anexo?: string;
  tipo_contribuyente?: 'persona_natural' | 'persona_juridica';
  clave_sol_usuario?: string;
  clave_sol_password?: string;
  allowed_document_types?: string[];
  default_certificate_id?: string;
  sunat_beta_url?: string;
  sunat_prod_url?: string;

  // GRE OAuth 2.0 credentials (for Guías de Remisión)
  gre_client_id?: string;
  gre_client_secret?: string;
  gre_email_autorizado?: string;
  gre_api_beta_url?: string;
  gre_api_prod_url?: string;
  gre_oauth_beta_url?: string;
  gre_oauth_prod_url?: string;
  gre_cdr_consulta_url?: string;

  // Detracción SPOT (Sistema de Pago de Obligaciones Tributarias)
  cuenta_banco_nacion?: string;

  // Certificate status fields (auto-assigned from certificate upload)
  has_digital_certificate?: boolean;
  certificate_id?: string;
  certificate_status?: string;
  certificate_uploaded_at?: string;
  certificate_ruc?: string;

  // API Key for external integrations
  api_key?: string;
}

export interface Certificate {
  id: string;
  tenant_id: string;
  tenant_name?: string;
  filename: string;
  issuer: string;
  subject: string;
  serial_number: string;
  valid_from: string;
  valid_to: string;
  status: string;
}

export interface SystemMetrics {
  timestamp: string;
  cpu_percent: number;
  memory_percent: number;
  disk_percent: number;
  redis_connected: boolean;
  services_status: Record<string, string>;
}

export interface Notification {
  id: string;
  title: string;
  message: string;
  type: string;
  category: string;
  tenant_id?: string;
  created_at: string;
  read: boolean;
}

export interface Document {
  id: string;
  document_type: string;
  series: string;
  number: number;
  document_number: string;
  issue_date: string;
  status: string;
  currency_code: string;
  total_amount: string;
  tenant_id: string;
  created_at: string;
  updated_at: string;
}

export interface DocumentCreateResponse {
  document_id: string;
  xml_generated: boolean;
  status: string;
  message?: string;
}

// User Management interfaces
export interface User {
  id: string;
  username: string;
  email: string;
  full_name: string;
  role: 'super_admin' | 'tenant_admin' | 'tenant_user' | 'api_client';
  tenant_id: string | null;
  is_active: boolean;
  permissions: string[];
  created_at: string;
  updated_at?: string;
  last_login?: string;
}

export interface UserCreate {
  username: string;
  email: string;
  password: string;
  full_name: string;
  role: 'super_admin' | 'tenant_admin' | 'tenant_user' | 'api_client';
  tenant_id?: string | null;
}

export interface UserUpdate {
  email?: string;
  full_name?: string;
  role?: 'super_admin' | 'tenant_admin' | 'tenant_user' | 'api_client';
  tenant_id?: string | null;
  is_active?: boolean;
  password?: string;
}

// Authentication API
export interface LoginRequest {
  username: string;
  password: string;
}

export interface LoginResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  refresh_token?: string;
  user_info?: {
    id: string;
    username: string;
    email: string;
    full_name: string;
    role: string;
    tenant_id: string | null;
    permissions: string[];
  };
}

export interface RegisterRequest {
  username: string;
  email: string;
  password: string;
  tenant_id: string;
  role?: string;
}

export const authApi = {
  login: (credentials: LoginRequest) =>
    authClient.post<LoginResponse>('/login', credentials),

  register: (data: RegisterRequest) =>
    authClient.post('/register', data),

  logout: () => {
    auth.removeToken();
    return Promise.resolve();
  },

  refreshToken: () =>
    authClient.post('/refresh', { token: auth.getToken() })
};

// API Methods
export const tenantsApi = {
  getAll: () => secureClient.get<{ tenants: Tenant[] }>('/api/v1/tenants'),
  getById: (id: string) => secureClient.get<Tenant>(`/api/v1/tenants/${id}`),
  create: (tenant: Partial<Tenant>) => secureClient.post<Tenant>('/api/v1/tenants', tenant),
  update: (id: string, tenant: Partial<Tenant>) =>
    secureClient.put<Tenant>(`/api/v1/tenants/${id}`, tenant),
  delete: (id: string) => secureClient.delete(`/api/v1/tenants/${id}`),
};

export const certificatesApi = {
  getAll: () => secureClient.get<{ certificates: Certificate[] }>('/api/v1/certificates'),
  getById: (id: string) => secureClient.get<Certificate>(`/api/v1/certificates/${id}`),
  getByTenant: (tenantId: string) =>
    secureClient.get<{ certificates: Certificate[] }>(`/api/v1/certificates?tenant_id=${tenantId}`),
  upload: (tenantId: string, file: File, password: string) => {
    const formData = new FormData();
    formData.append('tenant_id', tenantId);
    formData.append('file', file);
    formData.append('password', password);
    return secureClient.post('/api/v1/certificates/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  download: (id: string) => secureClient.get(`/api/v1/certificates/${id}/download`, {
    responseType: 'blob',
  }),
  delete: (id: string) => secureClient.delete(`/api/v1/certificates/${id}`),
};

export const analyticsApi = {
  getSystemMetrics: () => secureClient.get<SystemMetrics>('/api/v1/analytics/metrics/system'),
  getBusinessMetrics: () => secureClient.get('/api/v1/analytics/metrics/business'),
  getDashboardOverview: () => secureClient.get('/api/v1/analytics/dashboard/overview'),
  getHistoricalMetrics: (hours: number = 24) =>
    secureClient.get(`/api/v1/analytics/metrics/historical?hours=${hours}`),
};

export const notificationsApi = {
  getAll: (params?: { limit?: number; type?: string; category?: string }) =>
    secureClient.get<{ notifications: Notification[] }>('/api/v1/notifications', { params }),
  create: (notification: Partial<Notification>) =>
    secureClient.post('/api/v1/notifications', notification),
  markAsRead: (id: string) =>
    secureClient.put(`/api/v1/notifications/${id}/read`),
  getActiveAlerts: () =>
    secureClient.get('/api/v1/notifications/alerts/active'),
};

export const documentsApi = {
  getAll: (params?: { tenant_id?: string; status?: string; document_type?: string; page?: number; size?: number }) =>
    secureClient.get<{ documents: Document[]; total: number; page: number; size: number }>('/api/v1/documents', { params }),
  getById: (id: string) => secureClient.get<Document>(`/api/v1/documents/${id}`),
  create: (document: any, config?: any) => secureClient.post<DocumentCreateResponse>('/api/v1/documents', document, config),
  update: (id: string, document: Partial<Document>) =>
    secureClient.patch<Document>(`/api/v1/documents/${id}`, document),
  delete: (id: string) => secureClient.delete(`/api/v1/documents/${id}`),
  getXml: (id: string) => secureClient.get(`/api/v1/documents/${id}/xml`, {
    responseType: 'text',
  }),
};

export const sunatApi = {
  submitBatch: (data: { environment: string; batch_size: number }, config?: any) =>
    secureClient.post('/api/v1/sunat/submit-batch', data, config),
  getStatus: (documentId: string) => secureClient.get(`/api/v1/sunat/status/${documentId}`),
  queryDocument: (ruc: string, documentType: string, series: string, number: number) =>
    secureClient.get(`/api/v1/sunat/query`, {
      params: { ruc, document_type: documentType, series, number }
    }),
};

// API Public - External integrations with API Key authentication
export interface SimpleDocumentCreate {
  document_type: string;
  series_id?: string;
  currency?: string;
  exchange_rate?: number;
  issue_date?: string;
  due_date?: string;
  customer: {
    document_type: string;
    document_number: string;
    name: string;
    address?: string;
    email?: string;
  };
  items: Array<{
    description: string;
    quantity: number;
    unit_price: number;
    unit_code?: string;
    tax_type?: string;
    isc_category?: string;
    plastic_bags?: number;
  }>;
  notes?: string;
  auto_submit?: boolean;
}

export interface SimpleDocumentResponse {
  id: string;
  document_number: string;
  status: string;
  total_amount: number;
  currency: string;
  created_at: string;
  sunat_status?: string;
  sunat_response_code?: string;
}

export const createApiPublicApi = (apiKey: string) => {
  const client = createApiPublicClient(apiKey);

  return {
    // Documents
    createDocument: (document: SimpleDocumentCreate) =>
      client.post<SimpleDocumentResponse>('/v1/simple/documents', document),

    getDocument: (id: string) =>
      client.get<SimpleDocumentResponse>(`/v1/simple/documents/${id}`),

    listDocuments: (params?: { status?: string; document_type?: string; limit?: number }) =>
      client.get<{ documents: SimpleDocumentResponse[]; total: number }>('/v1/simple/documents', { params }),

    submitDocument: (id: string) =>
      client.post<{ success: boolean; sunat_response?: any }>(`/v1/simple/documents/${id}/submit`),

    voidDocument: (id: string, reason: string) =>
      client.post<{ success: boolean }>(`/v1/simple/documents/${id}/void`, { reason }),

    // Utilities
    getHealth: () =>
      client.get('/health'),

    getDocumentTypes: () =>
      client.get('/v1/utils/document-types'),

    getSeries: () =>
      client.get('/v1/series'),
  };
};

// Users API (Admin only)
export const usersApi = {
  getAll: (params?: { skip?: number; limit?: number }) =>
    authClient.get<{ users: User[]; total: number; page: number; size: number }>('/users', {
      params,
      headers: { Authorization: `Bearer ${auth.getToken()}` }
    }),
  getById: (id: string) =>
    authClient.get<User>(`/users/${id}`, {
      headers: { Authorization: `Bearer ${auth.getToken()}` }
    }),
  create: (user: UserCreate) =>
    authClient.post<User>('/users', user, {
      headers: { Authorization: `Bearer ${auth.getToken()}` }
    }),
  update: (id: string, user: UserUpdate) =>
    authClient.patch<User>(`/users/${id}`, user, {
      headers: { Authorization: `Bearer ${auth.getToken()}` }
    }),
  delete: (id: string) =>
    authClient.delete(`/users/${id}`, {
      headers: { Authorization: `Bearer ${auth.getToken()}` }
    }),
};

// Health check for services
// The Gateway provides a unified health check endpoint that monitors all services
// This is more efficient than checking each service individually
export const healthApi = {
  // Primary health check - Gateway monitors all backend services
  gateway: () => secureClient.get('/gateway/health'),

  // For compatibility, all health checks now use the unified gateway endpoint
  // The gateway response includes status for: auth, tenants, certificates, documents, sunat, customers, analytics, users
  analytics: () => secureClient.get('/gateway/health'),
  tenants: () => secureClient.get('/gateway/health'),
  certificates: () => secureClient.get('/gateway/health'),
  documents: () => secureClient.get('/gateway/health'),
  auth: () => secureClient.get('/gateway/health'),
  checkHealth: () => secureClient.get('/gateway/health'),
};

// Helper functions for backward compatibility with tests
export const getTenantById = async (tenantId: string): Promise<Tenant> => {
  const response = await tenantsApi.getById(tenantId);
  return response.data;
};

export const updateTenant = async (tenantId: string, data: Partial<Tenant>): Promise<Tenant> => {
  const response = await tenantsApi.update(tenantId, data);
  return response.data;
};